import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Container,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tooltip,
} from "@mui/material";
import {
  Search,
  Save,
  Cancel,
  Edit,
  EditNote,
  Close,
} from "@mui/icons-material";
import axios from "axios";

const EditClassLearners = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { schoolId } = location.state || {};

  const [className, setClassName] = useState("");
  const [learners, setLearners] = useState([]);
  const [originalLearners, setOriginalLearners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingLearners, setFetchingLearners] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("admissionNumber");
  const [editingLearner, setEditingLearner] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [editAllMode, setEditAllMode] = useState(false);
  const [schoolStreams, setSchoolStreams] = useState({});
  const [fetchingStreams, setFetchingStreams] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const classes = [
    "PG",
    "PP1",
    "PP2",
    "Grade 1",
    "Grade 2",
    "Grade 3",
    "Grade 4",
    "Grade 5",
    "Grade 6",
    "Grade 7",
    "Grade 8",
    "Grade 9",
    "Grade 10",
    "Grade 11",
    "Grade 12",
  ];

  const genders = ["M", "F"];

  // Fetch school streams when component mounts
  useEffect(() => {
    if (schoolId) {
      fetchSchoolStreams();
    }
  }, [schoolId]);

  useEffect(() => {
    if (className) {
      fetchLearnersByClass();
    } else {
      setLearners([]);
      setOriginalLearners([]);
    }
  }, [className]);

  const fetchSchoolStreams = async () => {
    setFetchingStreams(true);
    try {
      const response = await axios.get(
        `https://kidsm.vercel.app/api/schools/${schoolId}/streams`
      );
      if (response.data?.classStreams) {
        setSchoolStreams(response.data.classStreams);
      } else {
        setSchoolStreams({});
      }
    } catch (error) {
      console.error("Error fetching school streams:", error);
      setSchoolStreams({});
    } finally {
      setFetchingStreams(false);
    }
  };

  const fetchLearnersByClass = async () => {
    setFetchingLearners(true);
    try {
      const response = await axios.get(
        "https://kidsm.vercel.app/api/schools/learners/by-class",
        {
          params: { schoolId, className },
        }
      );

      if (Array.isArray(response.data)) {
        const learnersData = response.data.filter(
          (learner) => learner && learner._id
        );
        setLearners(learnersData);
        setOriginalLearners(JSON.parse(JSON.stringify(learnersData))); // Deep copy for comparison
      } else {
        setLearners([]);
        setOriginalLearners([]);
      }
    } catch (error) {
      console.error("Error fetching learners:", error);
      showSnackbar("Failed to fetch learners", "error");
      setLearners([]);
      setOriginalLearners([]);
    } finally {
      setFetchingLearners(false);
    }
  };

  const handleEditClick = (learner) => {
    setEditingLearner(learner);
    setEditFormData({
      fullName: learner.fullName || "",
      dateOfBirth: learner.dateOfBirth
        ? new Date(learner.dateOfBirth).toISOString().split("T")[0]
        : "",
      class: learner.class || "",
      stream: learner.stream || "",
      admissionNumber: learner.admissionNumber || "",
      birthCertificateNumber: learner.birthCertificateNumber || "",
      gender: learner.gender || "M",
      parentName: learner.parentName || "",
      parentContact: learner.parentContact || "",
      parentEmail: learner.parentEmail || "",
    });
    setOpenEditDialog(true);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveEdit = () => {
    const updatedLearners = learners.map((learner) =>
      learner._id === editingLearner._id
        ? { ...learner, ...editFormData }
        : learner
    );
    setLearners(updatedLearners);
    setOpenEditDialog(false);
    setEditingLearner(null);
    showSnackbar(
      "Learner details updated locally. Remember to save all changes.",
      "info"
    );
  };

  const handleCancelEdit = () => {
    setOpenEditDialog(false);
    setEditingLearner(null);
    setEditFormData({});
  };

  // Handle inline field changes in edit all mode
  const handleInlineFieldChange = (learnerId, field, value) => {
    const updatedLearners = learners.map((learner) =>
      learner._id === learnerId ? { ...learner, [field]: value } : learner
    );
    setLearners(updatedLearners);
  };

  // Toggle edit all mode
  const handleEditAllToggle = () => {
    if (editAllMode) {
      // If turning off edit all mode, revert any unsaved changes
      setLearners(JSON.parse(JSON.stringify(originalLearners)));
    }
    setEditAllMode(!editAllMode);
  };

  const handleBatchUpdate = async () => {
    // Find which learners have been modified
    const updates = learners
      .map((learner) => {
        const originalLearner = originalLearners.find(
          (ol) => ol._id === learner._id
        );
        if (!originalLearner) return null;

        // Check if any field has changed
        const fields = [
          "fullName",
          "dateOfBirth",
          "class",
          "stream",
          "admissionNumber",
          "birthCertificateNumber",
          "gender",
          "parentName",
          "parentContact",
          "parentEmail",
        ];
        const hasChanges = fields.some((field) => {
          const learnerValue = learner[field];
          const originalValue = originalLearner[field];

          if (field === "dateOfBirth") {
            return (
              new Date(learnerValue).getTime() !==
              new Date(originalValue).getTime()
            );
          }
          return learnerValue !== originalValue;
        });

        if (hasChanges) {
          return {
            learnerId: learner._id,
            updateData: {
              fullName: learner.fullName,
              dateOfBirth: learner.dateOfBirth,
              class: learner.class,
              stream: learner.stream,
              admissionNumber: learner.admissionNumber,
              birthCertificateNumber: learner.birthCertificateNumber,
              gender: learner.gender,
              parentName: learner.parentName,
              parentContact: learner.parentContact,
              parentEmail: learner.parentEmail,
            },
          };
        }
        return null;
      })
      .filter((update) => update !== null);

    if (updates.length === 0) {
      showSnackbar("No changes to save", "info");
      return;
    }

    setSaveLoading(true);
    try {
      const response = await axios.put(
        "https://kidsm.vercel.app/api/schools/learners/batch-update",
        {
          updates,
          schoolId,
        }
      );

      if (response.data.success) {
        showSnackbar("All learner updates saved successfully!", "success");
        // Refresh the data to get the updated versions
        fetchLearnersByClass();
        setEditAllMode(false);
      } else {
        showSnackbar(
          response.data.message || "Failed to save updates",
          "error"
        );
      }
    } catch (error) {
      console.error("Error updating learners:", error);
      showSnackbar(
        error.response?.data?.message || "Failed to save updates",
        "error"
      );
    } finally {
      setSaveLoading(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Filter learners based on search query
  const filteredLearners = learners.filter((learner) => {
    if (!searchQuery) return true;

    const query = searchQuery.toLowerCase();
    return (
      (learner.fullName || "").toLowerCase().includes(query) ||
      (learner.admissionNumber || "").toLowerCase().includes(query) ||
      (learner.birthCertificateNumber || "").toLowerCase().includes(query) ||
      (learner.parentName || "").toLowerCase().includes(query)
    );
  });

  // Sort learners
  const sortedLearners = [...filteredLearners].sort((a, b) => {
    if (sortBy === "name") {
      return (a.fullName || "").localeCompare(b.fullName || "");
    } else if (sortBy === "admissionNumber") {
      return (a.admissionNumber || "").localeCompare(b.admissionNumber || "");
    }
    return 0;
  });

  // Get available streams for a specific class
  const getStreamsForClass = (className) => {
    return schoolStreams[className] || [];
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <Container
      maxWidth="xl"
      sx={{ py: 4, bgcolor: "#f8f9fa", minHeight: "100vh" }}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Typography
          variant="h4"
          sx={{ fontWeight: "bold", color: "#2c3e50", mb: 3 }}
        >
          Edit Class Learners
        </Typography>

        {/* Class Selection */}
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select Class</InputLabel>
            <Select
              value={className}
              onChange={(e) => {
                setClassName(e.target.value);
                setEditAllMode(false);
              }}
              label="Select Class"
            >
              <MenuItem value="">Select a class</MenuItem>
              {classes.map((cls) => (
                <MenuItem key={cls} value={cls}>
                  {cls}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Search and Sort */}
        {className && (
          <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
            <TextField
              label="Search by name, admission, or parent"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              variant="outlined"
              sx={{ flex: 1, minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                label="Sort By"
              >
                <MenuItem value="admissionNumber">Admission Number</MenuItem>
                <MenuItem value="name">Name</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Learners Table */}
        {fetchingLearners ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mt: 4,
            }}
          >
            <CircularProgress color="primary" />
            <Typography sx={{ mt: 2, color: "#6200ee" }}>
              Fetching learners...
            </Typography>
          </Box>
        ) : sortedLearners.length > 0 ? (
          <Box sx={{ mt: 4 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", color: "#2c3e50" }}
              >
                Learners - {className} ({sortedLearners.length} students)
                {editAllMode && (
                  <Typography
                    component="span"
                    sx={{ color: "#ff9800", fontWeight: "bold", ml: 1 }}
                  >
                    • Editing Mode
                  </Typography>
                )}
              </Typography>

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Button
                  variant={editAllMode ? "outlined" : "contained"}
                  color={editAllMode ? "secondary" : "primary"}
                  startIcon={editAllMode ? <Close /> : <EditNote />}
                  onClick={handleEditAllToggle}
                  disabled={saveLoading}
                >
                  {editAllMode
                    ? "Cancel Edit All"
                    : "Edit All Learners at Once"}
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Save />}
                  onClick={handleBatchUpdate}
                  disabled={saveLoading}
                >
                  {saveLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    "Save All Changes"
                  )}
                </Button>
              </Box>
            </Box>

            <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#6200ee" }}>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Admission No.
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Full Name
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Class
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Stream
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Birth Cert. No.
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Gender
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Parent Name
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Parent Contact
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Parent Email
                    </TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedLearners.map((learner) => (
                    <TableRow key={learner._id} hover>
                      {/* Admission Number */}
                      <TableCell>
                        {editAllMode ? (
                          <TextField
                            size="small"
                            value={learner.admissionNumber || ""}
                            onChange={(e) =>
                              handleInlineFieldChange(
                                learner._id,
                                "admissionNumber",
                                e.target.value
                              )
                            }
                            fullWidth
                          />
                        ) : (
                          learner.admissionNumber
                        )}
                      </TableCell>

                      {/* Full Name */}
                      <TableCell>
                        {editAllMode ? (
                          <TextField
                            size="small"
                            value={learner.fullName || ""}
                            onChange={(e) =>
                              handleInlineFieldChange(
                                learner._id,
                                "fullName",
                                e.target.value
                              )
                            }
                            fullWidth
                          />
                        ) : (
                          learner.fullName
                        )}
                      </TableCell>

                      {/* Class */}
                      <TableCell>
                        {editAllMode ? (
                          <FormControl fullWidth size="small">
                            <Select
                              value={learner.class || ""}
                              onChange={(e) =>
                                handleInlineFieldChange(
                                  learner._id,
                                  "class",
                                  e.target.value
                                )
                              }
                            >
                              {classes.map((cls) => (
                                <MenuItem key={cls} value={cls}>
                                  {cls}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          learner.class
                        )}
                      </TableCell>

                      {/* Stream */}
                      <TableCell>
                        {editAllMode ? (
                          <FormControl fullWidth size="small">
                            <Select
                              value={learner.stream || ""}
                              onChange={(e) =>
                                handleInlineFieldChange(
                                  learner._id,
                                  "stream",
                                  e.target.value
                                )
                              }
                              displayEmpty
                            >
                              <MenuItem value="">
                                <em>No Stream</em>
                              </MenuItem>
                              {getStreamsForClass(learner.class).map(
                                (stream) => (
                                  <MenuItem key={stream} value={stream}>
                                    {stream}
                                  </MenuItem>
                                )
                              )}
                            </Select>
                          </FormControl>
                        ) : (
                          learner.stream || "-"
                        )}
                      </TableCell>

                      {/* Birth Certificate Number */}
                      <TableCell>
                        {editAllMode ? (
                          <TextField
                            size="small"
                            value={learner.birthCertificateNumber || ""}
                            onChange={(e) =>
                              handleInlineFieldChange(
                                learner._id,
                                "birthCertificateNumber",
                                e.target.value
                              )
                            }
                            fullWidth
                          />
                        ) : (
                          learner.birthCertificateNumber
                        )}
                      </TableCell>

                      {/* Gender */}
                      <TableCell>
                        {editAllMode ? (
                          <FormControl fullWidth size="small">
                            <Select
                              value={learner.gender || "M"}
                              onChange={(e) =>
                                handleInlineFieldChange(
                                  learner._id,
                                  "gender",
                                  e.target.value
                                )
                              }
                            >
                              <MenuItem value="M">Male</MenuItem>
                              <MenuItem value="F">Female</MenuItem>
                            </Select>
                          </FormControl>
                        ) : learner.gender === "M" ? (
                          "Male"
                        ) : (
                          "Female"
                        )}
                      </TableCell>

                      {/* Parent Name */}
                      <TableCell>
                        {editAllMode ? (
                          <TextField
                            size="small"
                            value={learner.parentName || ""}
                            onChange={(e) =>
                              handleInlineFieldChange(
                                learner._id,
                                "parentName",
                                e.target.value
                              )
                            }
                            fullWidth
                          />
                        ) : (
                          learner.parentName
                        )}
                      </TableCell>

                      {/* Parent Contact */}
                      <TableCell>
                        {editAllMode ? (
                          <TextField
                            size="small"
                            value={learner.parentContact || ""}
                            onChange={(e) =>
                              handleInlineFieldChange(
                                learner._id,
                                "parentContact",
                                e.target.value
                              )
                            }
                            fullWidth
                          />
                        ) : (
                          learner.parentContact
                        )}
                      </TableCell>

                      {/* Parent Email */}
                      <TableCell>
                        {editAllMode ? (
                          <TextField
                            size="small"
                            type="email"
                            value={learner.parentEmail || ""}
                            onChange={(e) =>
                              handleInlineFieldChange(
                                learner._id,
                                "parentEmail",
                                e.target.value
                              )
                            }
                            fullWidth
                          />
                        ) : (
                          learner.parentEmail || "-"
                        )}
                      </TableCell>

                      {/* Actions */}
                      <TableCell>
                        <Tooltip title="Edit individual learner">
                          <IconButton
                            color="primary"
                            onClick={() => handleEditClick(learner)}
                            disabled={editAllMode}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {editAllMode && (
              <Box sx={{ mt: 2, p: 2, bgcolor: "#fff3e0", borderRadius: 1 }}>
                <Typography variant="body2" sx={{ color: "#e65100" }}>
                  <strong>Editing Mode Active:</strong> You can edit all fields
                  directly in the table. Use dropdowns for Class, Stream, and
                  Gender fields. Stream options are limited to those registered
                  for each class in your school.
                </Typography>
              </Box>
            )}
          </Box>
        ) : className ? (
          <Card
            sx={{
              mt: 4,
              p: 3,
              textAlign: "center",
              boxShadow: 3,
              bgcolor: "#fff",
            }}
          >
            <Typography sx={{ fontWeight: "600", color: "#e74c3c", mb: 1 }}>
              No learners found for {className}
            </Typography>
            <Typography sx={{ color: "#666" }}>
              {searchQuery
                ? "No learners match your search criteria."
                : "There are no learners registered in this class."}
            </Typography>
          </Card>
        ) : null}

        {/* Edit Dialog */}
        <Dialog
          open={openEditDialog}
          onClose={handleCancelEdit}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Edit Learner: {editingLearner?.fullName}</DialogTitle>
          <DialogContent>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}
            >
              <TextField
                label="Full Name"
                name="fullName"
                value={editFormData.fullName || ""}
                onChange={handleEditFormChange}
                fullWidth
              />
              <TextField
                label="Date of Birth"
                name="dateOfBirth"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={editFormData.dateOfBirth || ""}
                onChange={handleEditFormChange}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Class</InputLabel>
                <Select
                  name="class"
                  value={editFormData.class || ""}
                  onChange={handleEditFormChange}
                  label="Class"
                >
                  {classes.map((cls) => (
                    <MenuItem key={cls} value={cls}>
                      {cls}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Stream</InputLabel>
                <Select
                  name="stream"
                  value={editFormData.stream || ""}
                  onChange={handleEditFormChange}
                  label="Stream"
                >
                  <MenuItem value="">
                    <em>No Stream</em>
                  </MenuItem>
                  {getStreamsForClass(editFormData.class).map((stream) => (
                    <MenuItem key={stream} value={stream}>
                      {stream}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Admission Number"
                name="admissionNumber"
                value={editFormData.admissionNumber || ""}
                onChange={handleEditFormChange}
                fullWidth
              />
              <TextField
                label="Birth Certificate Number"
                name="birthCertificateNumber"
                value={editFormData.birthCertificateNumber || ""}
                onChange={handleEditFormChange}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Gender</InputLabel>
                <Select
                  name="gender"
                  value={editFormData.gender || "M"}
                  onChange={handleEditFormChange}
                  label="Gender"
                >
                  <MenuItem value="M">Male</MenuItem>
                  <MenuItem value="F">Female</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Parent Name"
                name="parentName"
                value={editFormData.parentName || ""}
                onChange={handleEditFormChange}
                fullWidth
              />
              <TextField
                label="Parent Contact"
                name="parentContact"
                value={editFormData.parentContact || ""}
                onChange={handleEditFormChange}
                fullWidth
              />
              <TextField
                label="Parent Email"
                name="parentEmail"
                type="email"
                value={editFormData.parentEmail || ""}
                onChange={handleEditFormChange}
                fullWidth
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelEdit} startIcon={<Cancel />}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              variant="contained"
              startIcon={<Save />}
            >
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </motion.div>
    </Container>
  );
};

export default EditClassLearners;
